import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Bell, Home, Compass, Briefcase, MessageSquare, HelpCircle, LayoutDashboard, Building2, Users, LogOut, Settings, ChevronDown, ShieldCheck, BookOpen } from "lucide-react";
import Logo from "@/components/Logo";
import Avatar from "@/components/Avatar";
import OnboardingTour from "@/components/OnboardingTour";
import { AdminSidebarNav, AdminMobileNav, InvestorViewBanner } from "@/components/AdminNav";
import AdminSearch from "@/components/AdminSearch";
import { useAuth } from "@/lib/auth.jsx";
import { api } from "@/lib/api";
import { relativeTime } from "@/lib/utils";

const INVESTOR_NAV = [
  { to: "/home", label: "Home", icon: Home, testId: "nav-home" },
  { to: "/discover", label: "Discover", icon: Compass, testId: "nav-discover" },
  { to: "/portfolio", label: "Portfolio", icon: Briefcase, testId: "nav-portfolio" },
  { to: "/investor-guide", label: "Guide", icon: BookOpen, testId: "nav-guide" },
  { to: "/messages", label: "Messages", icon: MessageSquare, testId: "nav-messages" },
  { to: "/help", label: "Help", icon: HelpCircle, testId: "nav-help" },
];
const DEVELOPER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/properties", label: "Properties", icon: Building2, testId: "nav-properties" },
  { to: "/leads", label: "Leads", icon: Users, testId: "nav-leads" },
  { to: "/verification", label: "Verification", icon: ShieldCheck, testId: "nav-verification" },
  { to: "/messages", label: "Messages", icon: MessageSquare, testId: "nav-dev-messages" },
  { to: "/help", label: "Help", icon: HelpCircle, testId: "nav-dev-help" },
];

// Route a notification to the most relevant screen.
function notifTarget(n) {
  const d = n?.data || {};
  switch (n?.type) {
    case "new_interest":
      return d.conversation_id ? `/messages?c=${d.conversation_id}` : "/leads?filter=new_interest";
    case "new_message":
      return d.conversation_id ? `/messages?c=${d.conversation_id}` : "/messages";
    case "status_update":
      return d.conversation_id ? `/messages?c=${d.conversation_id}` : "/messages";
    case "development_update":
      return d.development_id ? `/development/${d.development_id}` : "/discover";
    case "development_review":
    case "development_approved":
    case "development_changes_requested":
      return d.development_id ? `/development/${d.development_id}` : "/properties";
    case "verification_document":
      return d.scope === "development" ? "/properties" : "/verification";
    case "support_reply":
    case "support_status":
      return "/help?tab=requests";
    case "new_signup":
      return d.user_id ? `/u/${d.user_id}` : "/admin";
    default:
      if (d.conversation_id) return `/messages?c=${d.conversation_id}`;
      if (d.development_id) return `/development/${d.development_id}`;
      if (d.user_id) return `/u/${d.user_id}`;
      return null;
  }
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const isAdmin = !!user?.is_admin;
  const inAdmin = location.pathname.startsWith("/admin");
  const nav = user?.role === "developer" ? DEVELOPER_NAV : INVESTOR_NAV;
  const profileRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  async function loadNotifs() {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch { /* ignore */ }
  }

  async function markAllRead() {
    try {
      await api.post("/notifications/read-all");
      setNotifs((n) => n.map((x) => ({ ...x, read: true })));
    } catch { /* ignore */ }
  }

  const unread = notifs.filter((n) => !n.read).length;

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <div className="min-h-screen flex bg-[var(--bg-app)]">
      {/* Sidebar */}
      <aside style={{ top: "var(--demo-banner-h, 0px)" }} className="hidden lg:flex w-64 fixed bottom-0 left-0 bg-white border-r border-neutral-200/70 flex-col z-30">
        <div className="px-6 py-5">
          <Link to={isAdmin ? "/admin" : user?.role === "developer" ? "/dashboard" : "/home"} data-testid="sidebar-logo">
            <Logo />
          </Link>
        </div>
        {isAdmin ? <AdminSidebarNav /> : <nav className="px-3 flex-1 flex flex-col gap-1">
          {nav.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? "bg-propera-light text-propera"
                    : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                }`
              }
            >
              <Icon size={18} strokeWidth={2} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>}
        <div className="px-3 pb-4 mt-auto">
          {!isAdmin && (
            <div className="px-4 py-3 rounded-2xl bg-gradient-to-br from-propera-light to-white border border-propera/10">
              <p className="text-xs font-semibold tracking-wider text-propera">DIASPORA INVESTING</p>
              <p className="text-sm text-neutral-700 mt-1 leading-snug">Build wealth back home with verified developments.</p>
            </div>
          )}
          <div className="px-2 mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-neutral-400">
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" data-testid="sidebar-privacy" className="hover:text-propera">Privacy</a>
            <a href="/terms.html" target="_blank" rel="noopener noreferrer" data-testid="sidebar-terms" className="hover:text-propera">Terms</a>
            <a href="/cookies.html" target="_blank" rel="noopener noreferrer" data-testid="sidebar-cookies" className="hover:text-propera">Cookies</a>
            <span className="text-neutral-300">· © 2026</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        {/* Topbar */}
        <header style={{ top: "var(--demo-banner-h, 0px)" }} className="sticky z-20 bg-white/80 backdrop-blur-xl border-b border-neutral-200/70">
          {isAdmin && !inAdmin && <InvestorViewBanner />}
          <div className="px-6 lg:px-10 h-16 flex items-center justify-between">
            <div className="lg:hidden">
              <Logo size={24} />
            </div>
            <div className="hidden lg:block text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold whitespace-nowrap">
              {isAdmin ? (inAdmin ? "Admin Console" : "Admin Console · Investor view") : user?.role === "developer" ? "Developer Portal" : "Investor Portal"}
            </div>
            {isAdmin && <div className="flex-1 flex justify-center px-3 lg:px-8 min-w-0"><AdminSearch /></div>}
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <div className="relative" ref={notifRef}>
                <button
                  data-testid="notification-bell"
                  onClick={() => setNotifOpen((o) => !o)}
                  className="relative p-2.5 rounded-full hover:bg-neutral-100 transition"
                  aria-label="Notifications"
                >
                  <Bell size={18} className="text-neutral-700" />
                  {unread > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
                  )}
                </button>
                {notifOpen && (
                  <div className="fixed sm:absolute inset-x-3 top-16 sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-96 bg-white rounded-2xl border border-neutral-200 shadow-xl overflow-hidden animate-fade-up z-50" data-testid="notification-panel">
                    <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
                      <span className="font-semibold text-neutral-900">Notifications</span>
                      {unread > 0 && (
                        <button data-testid="mark-all-read" onClick={markAllRead} className="text-xs text-propera font-medium hover:underline">Mark all read</button>
                      )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifs.length === 0 ? (
                        <div className="px-5 py-10 text-center text-sm text-neutral-500">No notifications yet</div>
                      ) : (
                        notifs.map((n) => {
                          const target = notifTarget(n);
                          return (
                            <button
                              key={n.id}
                              data-testid={`notif-${n.type}`}
                              onClick={async () => {
                                setNotifOpen(false);
                                if (!n.read) {
                                  try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
                                  setNotifs((arr) => arr.map((x) => x.id === n.id ? { ...x, read: true } : x));
                                }
                                if (target) navigate(target);
                              }}
                              className={`w-full text-left px-5 py-3 border-b border-neutral-50 transition hover:bg-neutral-50 ${!n.read ? "bg-propera-light/40" : ""}`}
                            >
                              <p className="text-sm font-medium text-neutral-900">{n.title}</p>
                              <p className="text-xs text-neutral-600 mt-0.5 line-clamp-2">{n.body}</p>
                              <p className="text-[10px] text-neutral-400 mt-1 uppercase tracking-wider">{relativeTime(n.created_at)}</p>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* Profile */}
              <div className="relative" ref={profileRef}>
                <button
                  data-testid="profile-menu-trigger"
                  onClick={() => setProfileOpen((o) => !o)}
                  className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full hover:bg-neutral-100 transition"
                >
                  <Avatar name={user?.name} src={user?.avatar} size={32} />
                  <ChevronDown size={14} className="text-neutral-500" />
                </button>
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl border border-neutral-200 shadow-xl overflow-hidden animate-fade-up" data-testid="profile-menu">
                    <div className="px-5 py-4 border-b border-neutral-100 flex items-center gap-3">
                      <Avatar name={user?.name} src={user?.avatar} size={40} />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-neutral-900 truncate">{user?.name}</p>
                        <p className="text-xs text-neutral-500 truncate">{user?.email}</p>
                      </div>
                    </div>
                    <button
                      data-testid="profile-edit"
                      onClick={() => { setProfileOpen(false); navigate("/profile"); }}
                      className="w-full flex items-center gap-3 px-5 py-3 text-sm text-neutral-700 hover:bg-neutral-50"
                    >
                      <Settings size={16} /> Edit Profile
                    </button>
                    {isAdmin && (
                      <button
                        data-testid="profile-admin-support"
                        onClick={() => { setProfileOpen(false); navigate("/admin"); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-sm text-neutral-700 hover:bg-neutral-50 border-t border-neutral-100"
                      >
                        <ShieldCheck size={16} className="text-propera" /> Admin Console
                      </button>
                    )}
                    <div className="border-t border-neutral-100 px-5 py-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-neutral-500">
                      <a href="/privacy.html" target="_blank" rel="noopener noreferrer" data-testid="profile-privacy" className="hover:text-propera">Privacy Policy</a>
                      <a href="/terms.html" target="_blank" rel="noopener noreferrer" data-testid="profile-terms" className="hover:text-propera">Terms</a>
                      <a href="/cookies.html" target="_blank" rel="noopener noreferrer" data-testid="profile-cookies" className="hover:text-propera">Cookies</a>
                    </div>
                    <button
                      data-testid="logout-button"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-5 py-3 text-sm text-red-600 hover:bg-red-50 border-t border-neutral-100"
                    >
                      <LogOut size={16} /> Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Mobile bottom nav */}
        <div className="lg:hidden fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-xl border-t border-neutral-200 z-30">
          {isAdmin ? <AdminMobileNav /> : <div className="flex items-center justify-around px-2 py-2">
            {nav.map(({ to, label, icon: Icon, testId }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`mobile-${testId}`}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 px-1.5 py-1.5 rounded-xl text-[10px] ${
                    isActive ? "text-propera" : "text-neutral-500"
                  }`
                }
              >
                <Icon size={20} />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>}
        </div>

        {/* Page */}
        <main className="flex-1 px-6 lg:px-10 py-8 pb-28 lg:pb-12 animate-fade-in">{children}</main>
      </div>

      <OnboardingTour />
    </div>
  );
}
